import { useState, useEffect, ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert, { AlertVariant } from '../../components/Alert';

import Input from '../../components/Input';
import Password from '../../components/Password';
import Button from '../../components/Button';
import Typography from '../../components/Typography';

import HtmlInput from '../../interfaces/HtmlInput';
import validations from '../../helpers/Validations';
import AuthService from '../../services/AuthService';
import { setStorageWithTtl } from '../../helpers/Storage';
import {
  gtmAttributesApiError,
  gtmAttributesPassword,
  gtmAttributesRememberUser,
  gtmAttributesRegistration,
  gtmAttributesUpdateLoginPage,
} from '../../utils/gtmVariables';
import useGTMEventsTracker from '../../hooks/useGTMEventsTracker';
import Container from '../../components/Container';

const Login = () => {
  const [show, setShow] = useState(false);
  const [variant, setVariant] = useState<AlertVariant | undefined>(undefined);
  const [message, setMessage] = useState<ReactElement>();
  const [email, setEmail] = useState<string | undefined>('');
  const [password, setPassword] = useState<string | undefined>('');
  const navigate = useNavigate();
  const gtmEventsTracker = useGTMEventsTracker();

  useEffect(() => {
    gtmEventsTracker(gtmAttributesUpdateLoginPage);
  }, []);

  const setFieldValue = (e: any, callback: Function) => {
    if (e.target && typeof e.target.value !== undefined) {
      callback(e.target.value);
    }
  };

  const userInactive = () => {
    setStorageWithTtl('sellerEmail', email ?? '', 1440);
    navigate('/confirmation-email');
  };

  const anotherError = () => {
    setVariant('warning');
    gtmEventsTracker(gtmAttributesApiError);
    setMessage(
      <>
        Email ou senha incorretos.
        <strong> Por favor, tente novamente!</strong>
      </>
    );
    setShow(true);
    setTimeout(() => {
      setShow(false);
    }, 3000);
    return AuthService.logout();
  };

  const login = () => {
    if (email !== '' && password !== '') {
      AuthService.login({ email, password })
        .then(() => {
          navigate('/');
        })
        .catch((error: { response: { status: number; data: { message: string } } }) => {
          if (error.response && error.response.data.message === 'user_inactive') {
            return userInactive();
          }

          return anotherError();
        });
    } else {
      gtmEventsTracker(gtmAttributesApiError);
      setVariant('warning');
      setMessage(
        <>
          Email ou senha não informados.
          <strong> Por favor, tente novamente!</strong>
        </>
      );
      setShow(true);
      setTimeout(() => {
        setShow(false);
      }, 3000);
    }
  };

  return (
    <div id="mp__login" className="flex items-center justify-center max-[768px]:items-start">
      <Container className="w-full flex justify-center">
        <form
          className="bg-neutral-light/20 max-w-[712px] w-full rounded-lg px-28 py-16 mt-12 mb-28 max-[768px]:px-0 max-[768px]:pt-[120px] max-[768px]:pb-0 max-[768px]:bg-transparent max-[768px]:gap-6 max-[256px]:mt-0 max-[256px]:mb-[75px]"
          onSubmit={(event) => {
            event.preventDefault();
            login();
          }}
        >
          <div className="flex flex-col gap-6">
            <Input
              type="text"
              label="Email"
              name="email"
              className="email-input"
              value={email}
              rules={[(val: HtmlInput) => validations.isValidEmail(val, true)]}
              onChange={(e: any) => setFieldValue(e, setEmail)}
            />
            <Password
              onChange={(e: any) => setFieldValue(e, setPassword)}
              loginPassword={true}
              inputProps={{ value: password }}
              gtmAttributes={gtmAttributesPassword}
            />
          </div>
          <div className="flex flex-col gap-6 mt-12">
            <Button type="submit" text="Acessar" id="mp__login_access-button" />
            <div className="text-center mt-6 text-neutral-darkest">
              <Typography as="span" variant="body2">
                Ainda não tem seu cadastro?{' '}
                <Typography variant="subtitle2" className="text-support2-medium">
                  <a
                    href="/"
                    onClick={() => gtmEventsTracker(gtmAttributesRegistration)}
                    className="font-bold"
                  >
                    Clique aqui
                  </a>
                </Typography>
              </Typography>
            </div>
          </div>
        </form>
        <div className="absolute top-[3.688rem] right-[5.5rem] w-[23.625rem] max-[768px]:top-[4.688rem] max-[768px]:w-full max-[768px]:right-0 max-[768px]:px-5 max-[768px]:py-6">
          <Alert
            show={show}
            variant={variant}
            onClose={() => setShow(false)}
            dismissible
            closeVariant="white"
          >
            <p>{message}</p>
          </Alert>
        </div>
      </Container>
    </div>
  );
};

export default Login;
